import React from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CCard, CCardBody } from '@coreui/react-pro'
import { ScryerSpark } from '@scryerlabs/ui'

interface FailureCardProps {
  title: string
  message: ReactNode
  /** Quoted by whoever hit the failure, so their report can be matched to the recorded one. */
  reference?: string | null
  /** Diagnostic text shown below a rule. Never server-supplied exception text. */
  details?: string | null
  /** Buttons. Rendered above the rule. */
  children?: ReactNode
}

/** The full-screen "we could not do the thing" card. */
const FailureCard: React.FC<FailureCardProps> = ({ title, message, reference, details, children }) => {
  const { t } = useTranslation('common')

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body p-3">
      <CCard className="text-center shadow-sm" style={{ maxWidth: '24rem', width: '100%' }}>
        <CCardBody className="p-4">
          <ScryerSpark size={40} className="mb-3" />
          <h1 className="h5 mb-2">{title}</h1>
          <p className="text-body-secondary mb-4">{message}</p>
          {children}
          {(reference || details) && (
            <div className="text-body-secondary small mt-4 pt-3 border-top">
              {reference && (
                <>
                  <div className="mb-1">{t('failure.quoteReference')}</div>
                  {/* user-select-all so one click selects the whole id - it is long, and a
                      half-copied one resolves to nothing. */}
                  <code className="user-select-all text-body">{reference}</code>
                </>
              )}
              {details && (
                <div className={reference ? 'mt-2' : undefined}>
                  {t('failure.details')} <code>{details}</code>
                </div>
              )}
            </div>
          )}
        </CCardBody>
      </CCard>
    </div>
  )
}

export default FailureCard
